<script setup lang="ts">
/**
 * Column browser (Miller columns, as in the macOS Finder): each folder opens
 * its children in a new column to the right, the row stays marked; the view
 * scrolls to the newest column. Children come from `loadChildren(item)`
 * (async, with a loading and an error state per column).
 *
 * Keyboard: ↑/↓ move within a column, → opens a folder and moves into it,
 * ← goes back to the parent column, Enter opens a file (`open`). A click
 * selects (`select`), a double click on a file opens it. The `item` slot
 * replaces a row's content; by default a type icon, the name and `meta`.
 * `layout="separate"` keeps equal-width, individually framed columns with
 * space between them. Their width follows a third of the content viewport,
 * bounded by the kit's panel steps; `joined` fills one shared surface.
 */
import ChevronRight from 'lucide-vue-next/dist/esm/icons/chevron-right.js';
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import NxFileTypeIcon from '../Attachment/NxFileTypeIcon.vue';
import { useNativeDropTarget } from '../FileDrop/useNativeDropTarget';

export interface NxColumnItem {
  key: string | number;
  /** Shown text; also the file name the type icon is guessed from. */
  label: string;
  folder?: boolean;
  /** MIME type, for the icon. */
  type?: string;
  /** Muted second line (size, date). */
  meta?: string;
}

/** A host-controlled trail, for cached navigation and deep links. */
export interface NxColumnBrowserColumn {
  key: string | number;
  label: string;
  items: readonly NxColumnItem[];
  selectedKey?: NxColumnItem['key'] | null;
  /** An independently controlled multi-selection; navigation still uses selectedKey. */
  selectedKeys?: readonly NxColumnItem['key'][];
  loading?: boolean;
  error?: boolean;
  /** A contextual empty state, e.g. a search with no matches. */
  emptyLabel?: string;
}

interface Column {
  label?: string;
  parent: NxColumnItem | null;
  items: NxColumnItem[];
  loading: boolean;
  error: boolean;
  emptyLabel?: string;
  /** Key of the marked row in this column. */
  selected: NxColumnItem['key'] | null;
  selectedKeys?: readonly NxColumnItem['key'][];
}

const props = withDefaults(
  defineProps<{
    /** The first column. */
    items?: readonly NxColumnItem[];
    loadChildren?: (item: NxColumnItem) => Promise<NxColumnItem[]>;
    /** Host owns the trail and data; select requests navigation, without local loading. */
    columns?: readonly NxColumnBrowserColumn[];
    layout?: 'joined' | 'separate';
    rootLabel?: string;
    emptyLabel?: string;
    loadingLabel?: string;
    errorLabel?: string;
    ariaLabel?: string;
    /** Consumer-owned native drags, independent from navigation and selection. */
    draggableItems?: boolean;
    /** Whether the native payload may be dropped onto this item. */
    canDropItem?: (event: DragEvent, item: NxColumnItem) => boolean;
    itemDropLabel?: (item: NxColumnItem) => string;
  }>(),
  {
    items: () => [],
    loadChildren: undefined,
    columns: undefined,
    layout: 'joined',
    rootLabel: 'Start',
    emptyLabel: 'Keine Elemente',
    loadingLabel: 'Wird geladen …',
    errorLabel: 'Konnte nicht geladen werden.',
    ariaLabel: 'Spaltenansicht',
    draggableItems: false,
    canDropItem: undefined,
    itemDropLabel: undefined,
  }
);

const emit = defineEmits<{
  select: [item: NxColumnItem, path: NxColumnItem[], columnIndex: number];
  selectionRequest: [
    item: NxColumnItem,
    event: MouseEvent | KeyboardEvent,
    columnIndex: number,
    toggle: boolean,
  ];
  open: [item: NxColumnItem, path: NxColumnItem[]];
  /** Context-menu request; position/dismissal belong to NxContextMenu. */
  contextmenu: [event: MouseEvent, item: NxColumnItem, path: NxColumnItem[], columnIndex: number];
  itemDragStart: [event: DragEvent, item: NxColumnItem, columnIndex: number];
  itemDragEnd: [event: DragEvent];
  itemDrop: [event: DragEvent, item: NxColumnItem, columnIndex: number];
}>();

const dropTarget = useNativeDropTarget<{ item: NxColumnItem; columnIndex: number }>({
  enabled: () => Boolean(props.canDropItem),
  key: ({ item, columnIndex }) => JSON.stringify([columnIndex, item.key]),
  canDrop: (event, { item }) => props.canDropItem?.(event, item) ?? false,
  onDrop: (event, { item, columnIndex }) => emit('itemDrop', event, item, columnIndex),
  label: ({ item }) => props.itemDropLabel?.(item) ?? item.label,
});

let nativeDragStarted = false;
function onDragStart(event: DragEvent, item: NxColumnItem, columnIndex: number) {
  if (!props.draggableItems) return;
  event.stopPropagation();
  emit('itemDragStart', event, item, columnIndex);
  nativeDragStarted = !event.defaultPrevented;
}

function onDragEnd(event: DragEvent) {
  dropTarget.reset();
  if (nativeDragStarted) emit('itemDragEnd', event);
  nativeDragStarted = false;
}

const state = ref<Column[]>([]);
const columns = computed<Column[]>(
  () =>
    props.columns?.map((column) => ({
      parent: null,
      label: column.label,
      items: [...column.items],
      selected: column.selectedKey ?? null,
      selectedKeys: column.selectedKeys,
      loading: Boolean(column.loading),
      error: Boolean(column.error),
      emptyLabel: column.emptyLabel,
    })) ?? state.value
);
const scroller = useTemplateRef<HTMLElement>('scroller');
let loadSeq = 0;
let focusChildOf: number | null = null;

function reset() {
  loadSeq += 1;
  state.value = [
    { parent: null, items: [...props.items], loading: false, error: false, selected: null },
  ];
}
reset();
watch(() => props.items, reset);
watch(
  () => props.columns?.map((column) => column.key).join('|'),
  () => {
    void scrollToEnd();
  },
  { flush: 'post', immediate: true }
);
watch(
  () => props.columns,
  () => {
    const index = focusChildOf;
    if (index === null || !columns.value[index + 1]?.items.length) return;
    focusChildOf = null;
    void nextTick(() => focusRow(index + 1, 0));
  },
  { flush: 'post' }
);

/** The chain of selected folders up to (and including) column `index`'s selection. */
function pathTo(index: number): NxColumnItem[] {
  const out: NxColumnItem[] = [];
  for (let i = 0; i <= index; i++) {
    const col = columns.value[i];
    const item = col?.items.find((it) => it.key === col.selected);
    if (item) out.push(item);
  }
  return out;
}

async function choose(index: number, item: NxColumnItem, focus = false, event?: MouseEvent) {
  const col = columns.value[index];
  if (!col) return;
  if (col.selectedKeys && event && (event.ctrlKey || event.metaKey || event.shiftKey)) {
    emit('selectionRequest', item, event, index, false);
    return;
  }
  if (props.columns) {
    focusChildOf = focus && item.folder ? index : null;
    emit('select', item, [...pathTo(index - 1), item], index);
    return;
  }
  const seq = ++loadSeq;
  col.selected = item.key;
  state.value = columns.value.slice(0, index + 1);
  emit('select', item, pathTo(index), index);
  if (!item.folder) return;
  const child: Column = { parent: item, items: [], loading: true, error: false, selected: null };
  state.value = [...columns.value, child];
  await scrollToEnd();
  try {
    const items = (await props.loadChildren?.(item)) ?? [];
    if (seq !== loadSeq) return;
    child.items = items;
  } catch {
    if (seq !== loadSeq) return;
    child.error = true;
  } finally {
    if (seq === loadSeq) child.loading = false;
  }
  if (seq !== loadSeq) return;
  state.value = [...columns.value];
  if (focus) {
    await nextTick();
    focusRow(index + 1, 0);
  }
}

async function scrollToEnd() {
  await nextTick();
  const el = scroller.value;
  if (el) el.scrollTo?.({ left: el.scrollWidth, behavior: 'smooth' });
}

function focusRow(col: number, row: number) {
  scroller.value?.querySelector<HTMLElement>(`[data-col="${col}"][data-row="${row}"]`)?.focus();
}

function onKeydown(event: KeyboardEvent, col: number, row: number, item: NxColumnItem) {
  const items = columns.value[col]?.items ?? [];
  switch (event.key) {
    case ' ':
      if (!columns.value[col]?.selectedKeys) return;
      event.preventDefault();
      emit('selectionRequest', item, event, col, true);
      break;
    case 'ArrowDown':
      event.preventDefault();
      focusRow(col, Math.min(row + 1, items.length - 1));
      break;
    case 'ArrowUp':
      event.preventDefault();
      focusRow(col, Math.max(row - 1, 0));
      break;
    case 'ArrowRight':
      if (!item.folder) return;
      event.preventDefault();
      void choose(col, item, true);
      break;
    case 'ArrowLeft': {
      if (col === 0) return;
      event.preventDefault();
      const parent = columns.value[col - 1];
      const at = parent?.items.findIndex((it) => it.key === parent.selected) ?? 0;
      focusRow(col - 1, Math.max(0, at));
      break;
    }
    case 'Enter':
      event.preventDefault();
      if (item.folder) void choose(col, item, true);
      else emit('open', item, [...pathTo(col - 1), item]);
      break;
    default:
  }
}

function onDblClick(col: number, item: NxColumnItem) {
  if (!item.folder) emit('open', item, [...pathTo(col - 1), item]);
}

/** The row that takes the tab stop in a column: the marked one, else the first. */
function tabRow(col: number): number {
  const c = columns.value[col];
  const at = c?.items.findIndex((it) => it.key === c.selected) ?? -1;
  return at >= 0 ? at : 0;
}

const lastColumn = computed(() => columns.value.length - 1);
function isSelected(column: Column, item: NxColumnItem) {
  return column.selectedKeys
    ? column.selectedKeys.includes(item.key)
    : column.selected === item.key;
}

function onContextMenu(event: MouseEvent, column: number, item: NxColumnItem) {
  // A browser menu stays available when the host has no action menu.
  emit('contextmenu', event, item, [...pathTo(column - 1), item], column);
}
</script>

<template>
  <div
    ref="scroller"
    class="nx-column-browser"
    :class="`layout-${props.layout}`"
    role="group"
    :aria-label="props.ariaLabel"
  >
    <section
      v-for="(column, ci) in columns"
      :key="ci"
      class="nx-column-browser__column"
      :class="{ 'is-last': ci === lastColumn }"
      :aria-label="column.label ?? column.parent?.label ?? props.rootLabel"
    >
      <header class="nx-column-browser__head">
        <strong :title="column.label ?? column.parent?.label ?? props.rootLabel">
          {{ column.label ?? column.parent?.label ?? props.rootLabel }}
        </strong>
        <small v-if="!column.loading && !column.error">{{ column.items.length }}</small>
      </header>
      <div v-if="column.loading" class="nx-column-browser__state" role="status">
        <span class="nx-column-browser__spinner" aria-hidden="true" />
        {{ props.loadingLabel }}
      </div>
      <div v-else-if="column.error" class="nx-column-browser__state is-error" role="alert">
        {{ props.errorLabel }}
      </div>
      <div v-else-if="!column.items.length" class="nx-column-browser__state">
        {{ column.emptyLabel ?? props.emptyLabel }}
      </div>
      <ul
        v-else
        class="nx-column-browser__list"
        role="listbox"
        :aria-multiselectable="column.selectedKeys ? 'true' : undefined"
        :aria-label="column.label ?? column.parent?.label ?? props.rootLabel"
      >
        <li
          v-for="(item, ri) in column.items"
          :key="item.key"
          class="nx-column-browser__row"
          :class="{
            'is-selected': isSelected(column, item),
            'is-drop-target': dropTarget.isActive({ item, columnIndex: ci }),
            'is-trail':
              column.selected === item.key &&
              ci < lastColumn &&
              !column.selectedKeys?.includes(item.key),
          }"
          role="option"
          :aria-selected="isSelected(column, item)"
          :tabindex="ri === tabRow(ci) ? 0 : -1"
          :data-col="ci"
          :data-row="ri"
          :draggable="props.draggableItems || undefined"
          :aria-description="
            dropTarget.isActive({ item, columnIndex: ci })
              ? (props.itemDropLabel?.(item) ?? item.label)
              : undefined
          "
          @click="choose(ci, item, false, $event)"
          @dblclick="onDblClick(ci, item)"
          @keydown="onKeydown($event, ci, ri, item)"
          @contextmenu="onContextMenu($event, ci, item)"
          @dragstart="onDragStart($event, item, ci)"
          @dragend="onDragEnd"
          @dragenter="dropTarget.dragenter($event, { item, columnIndex: ci })"
          @dragover="dropTarget.dragover($event, { item, columnIndex: ci })"
          @dragleave="dropTarget.dragleave"
          @drop="dropTarget.drop($event, { item, columnIndex: ci })"
        >
          <slot name="item" :item="item" :selected="isSelected(column, item)">
            <NxFileTypeIcon
              :kind="item.folder ? 'folder' : undefined"
              :name="item.label"
              :type="item.type"
              size="sm"
            />
            <span class="nx-column-browser__text">
              <span class="nx-column-browser__name" :title="item.label">{{ item.label }}</span>
              <span v-if="item.meta" class="nx-column-browser__meta">{{ item.meta }}</span>
              <span v-if="$slots['item-after']" class="nx-column-browser__markers">
                <slot name="item-after" :item="item" />
              </span>
            </span>
            <ChevronRight
              v-if="item.folder"
              class="nx-column-browser__chevron nxt-icon-sm"
              aria-hidden="true"
            />
          </slot>
        </li>
      </ul>
    </section>
    <span v-if="canDropItem" class="nx-column-browser__sr" role="status" aria-live="polite">
      {{ dropTarget.label.value }}
    </span>
  </div>
</template>

<style scoped>
.nx-column-browser {
  box-sizing: border-box;
  display: flex;
  flex: 1 1 auto;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scroll-snap-type: x proximity;
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-lg);
  background: var(--nxt-surface-bg);
}
.nx-column-browser__column {
  display: flex;
  flex: 0 0 var(--nxt-panel-width-sm);
  flex-direction: column;
  min-height: 0;
  border-inline-end: var(--nxt-hairline) solid var(--nxt-border);
  scroll-snap-align: start;
  animation: nx-column-in var(--nxt-motion-base) var(--nxt-ease-out);
}
.nx-column-browser__column.is-last {
  flex-grow: 1;
  border-inline-end: 0;
}
.nx-column-browser.layout-separate {
  gap: var(--nxt-space-sm);
  padding: var(--nxt-space-md);
  border: 0;
  border-radius: 0;
  background: transparent;
}
.nx-column-browser.layout-separate .nx-column-browser__column {
  flex: 0 0
    clamp(
      var(--nxt-panel-width-lg),
      calc((100% - 2 * var(--nxt-space-sm)) / 3),
      var(--nxt-panel-width-4xl)
    );
  min-width: 0;
  overflow: hidden;
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-surface-bg);
}
.nx-column-browser__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  border-block-end: var(--nxt-hairline) solid var(--nxt-border-subtle);
  font-size: var(--nxt-font-size-xs);
}
.nx-column-browser__head strong {
  overflow: hidden;
  color: var(--nxt-fg);
  font-weight: var(--nxt-font-weight-semibold);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-column-browser__head small {
  color: var(--nxt-fg-muted);
  font-variant-numeric: tabular-nums;
}
.nx-column-browser__list {
  flex: 1 1 auto;
  min-height: 0;
  margin: 0;
  padding: var(--nxt-space-2xs);
  overflow-y: auto;
  font-size: inherit;
  list-style: none;
}
.nx-column-browser__row {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  border-radius: var(--nxt-radius-sm);
  color: var(--nxt-fg);
  cursor: default;
  user-select: none;
}
.nx-column-browser__row:hover {
  background: var(--nxt-table-row-hover-bg);
}
.nx-column-browser__row.is-selected {
  background: var(--nxt-brand-bg);
  color: var(--nxt-fg-on-accent);
}
/* A folder already opened to the right: marked, but no longer the focus. */
.nx-column-browser__row.is-trail {
  background: var(--nxt-brand-subtle-bg);
  color: var(--nxt-brand-subtle-fg);
}
.nx-column-browser__row:focus-visible {
  outline: none;
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-column-browser__row.is-drop-target {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-brand-border);
  outline-offset: calc(var(--nxt-hairline) * -2);
  background: var(--nxt-brand-subtle-bg);
  color: var(--nxt-brand-subtle-fg);
}
.nx-column-browser__sr {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
.nx-column-browser__text {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
  line-height: var(--nxt-line-height-tight);
}
.nx-column-browser__markers {
  display: flex;
  flex-wrap: wrap;
  gap: var(--nxt-space-2xs);
  margin-block-start: var(--nxt-space-2xs);
}
.nx-column-browser__markers:empty {
  display: none;
}
.nx-column-browser__name {
  overflow: hidden;
  font-size: var(--nxt-font-size-sm);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-column-browser__meta {
  overflow: hidden;
  font-size: var(--nxt-font-size-xs);
  opacity: 0.7;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-column-browser__chevron {
  flex: 0 0 auto;
  opacity: 0.6;
}
.nx-column-browser__state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-lg) var(--nxt-space-sm);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
  text-align: center;
}
.nx-column-browser__state.is-error {
  color: var(--nxt-danger-subtle-fg);
}
.nx-column-browser__spinner {
  width: var(--nxt-dot-size-lg);
  height: var(--nxt-dot-size-lg);
  border: calc(var(--nxt-hairline) * 2) solid var(--nxt-border);
  border-top-color: var(--nxt-brand-bg);
  border-radius: var(--nxt-radius-full);
  animation: nx-column-spin 0.8s linear infinite;
}
@keyframes nx-column-in {
  from {
    opacity: 0;
    transform: translateX(calc(var(--nxt-size-unit) * 0.5));
  }
}
@keyframes nx-column-spin {
  to {
    transform: rotate(360deg);
  }
}
@media (prefers-reduced-motion: reduce) {
  .nx-column-browser__column {
    animation: none;
  }
  .nx-column-browser__spinner {
    animation-duration: 3s;
  }
}
</style>
